/**
 * A very small DOM builder.
 *
 * No framework: the screens here are rebuilt wholesale on navigation and
 * patched in place during play, and forty lines of helpers cover all of it.
 */

export type Child = Node | string | number | null | undefined | false;

export interface Props {
  class?: string;
  text?: string;
  /** Inline styles, as CSS custom properties or plain declarations. */
  style?: Record<string, string | number>;
  /** `data-*` attributes. */
  data?: Record<string, string | number | boolean | undefined>;
  /** Everything else: attributes written verbatim. */
  attrs?: Record<string, string | number | boolean | null | undefined>;
  on?: Partial<Record<keyof HTMLElementEventMap, (e: never) => void>>;
  ref?: (node: HTMLElement) => void;
}

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Props = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  if (props.class) node.className = props.class;
  if (props.text !== undefined) node.textContent = props.text;

  if (props.style) {
    for (const [k, v] of Object.entries(props.style)) {
      if (v === '' || v === undefined) continue;
      if (k.startsWith('--')) node.style.setProperty(k, String(v));
      else node.style.setProperty(k, String(v));
    }
  }

  if (props.data) {
    for (const [k, v] of Object.entries(props.data)) {
      if (v === undefined || v === false) continue;
      node.dataset[k] = v === true ? 'true' : String(v);
    }
  }

  if (props.attrs) {
    for (const [k, v] of Object.entries(props.attrs)) {
      if (v === null || v === undefined || v === false) continue;
      node.setAttribute(k, v === true ? '' : String(v));
    }
  }

  if (props.on) {
    for (const [k, fn] of Object.entries(props.on)) {
      node.addEventListener(k, fn as EventListener);
    }
  }

  append(node, children);
  props.ref?.(node);
  return node;
}

export function append(parent: Node, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === 'object' ? child : document.createTextNode(String(child)));
  }
}

export function svg(path: string, viewBox = '0 0 24 24'): SVGSVGElement {
  const node = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  node.setAttribute('viewBox', viewBox);
  node.setAttribute('aria-hidden', 'true');
  node.setAttribute('fill', 'none');
  const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  p.setAttribute('d', path);
  node.appendChild(p);
  return node;
}

export function clear(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function mount(parent: Node, ...children: Child[]): void {
  clear(parent);
  append(parent, children);
}

export function el<T extends HTMLElement = HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing #${id}`);
  return node as T;
}

/** Icons, as single path strings. */
export const ICON = {
  help: 'M9.1 9a3 3 0 1 1 4.2 2.8c-.8.4-1.3 1.1-1.3 2v.4M12 17.5h.01M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z',
  stats: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  archive: 'M3 7h18v4H3zM5 11v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8M10 15h4',
  back: 'M15 5l-7 7 7 7',
  close: 'M6 6l12 12M18 6L6 18',
  undo: 'M4 9h11a5 5 0 0 1 0 10H8M4 9l4-4M4 9l4 4',
  share: 'M12 3v12M12 3L8 7M12 3l4 4M5 14v6a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-6',
  play: 'M7 4.5v15l13-7.5z',
  sound: 'M11 5 6 9H3v6h3l5 4zM16 9a4 4 0 0 1 0 6',
  mute: 'M11 5 6 9H3v6h3l5 4zM16.5 9.5l5 5M21.5 9.5l-5 5',
  check: 'M4 12.5 9 17.5 20 6.5',
  replay: 'M20 12a8 8 0 1 1-2.3-5.6M20 4v5h-5',
} as const;
